import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { CategoryKind, Product } from '@/services/catalog.service';
import { useAuthStore } from '@/stores/auth.store';
import { pickLocalized } from '@/i18n';
import { formatUZS } from '@/shared/lib/format';
import { formatProductPrice } from '@/shared/lib/price';
import { haptic } from '@/shared/lib/telegram';
import { cn } from '@/shared/lib/cn';
import { CategoryIcon, GiftIcon, giftKindFromSku, NftGem, nftTierFromSku } from './CategoryIcon';

// Categories that show the "⚡ Auto" delivery chip. Telegram Premium was
// removed at the user's request — they don't want the lightning chip cluttering
// the Premium cards (it's also redundant since the whole catalog is auto).
const AUTO_DELIVERY: CategoryKind[] = ['telegram_stars'];

function discountPercent(price: string, oldPrice: string | null): number | null {
  if (!oldPrice) return null;
  const p = Number(price);
  const op = Number(oldPrice);
  if (!op || op <= p) return null;
  return Math.round((1 - p / op) * 100);
}

function formatMeta(product: Product, lang: 'uz' | 'ru' | 'en'): string | null {
  if (product.durationDays != null && product.durationDays > 0) {
    const d = product.durationDays;
    return lang === 'ru' ? `${d} дн.` : lang === 'en' ? `${d} days` : `${d} kun`;
  }
  return null;
}

const CARD_SURFACE =
  'product-card relative overflow-hidden rounded-2xl bg-gradient-to-b from-[#212126] to-[#141416] ring-1 ring-white/[0.07]';
const CARD_SHADOW =
  '0 14px 32px rgba(0,0,0,0.55), 0 4px 8px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.4)';

const ENTER = {
  initial: { opacity: 0, y: 8, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { duration: 0.32, ease: [0.2, 0.7, 0.2, 1] as const },
};

export function ProductCard({
  product,
  variant = 'list',
}: {
  product: Product;
  variant?: 'list' | 'grid' | 'stars' | 'hero';
}) {
  // VIP products get their own premium design in every view so the card always
  // stands out from the regular catalog cards.
  if (product.meta?.vip) return <VipCard product={product} variant={variant} />;
  // NFT giftlar — kolleksion premium karta (har doim to'liq kenglikda).
  if (product.category?.kind === 'telegram_nft') return <NftCard product={product} />;
  // Telegram giftlar — haqiqiy Telegram gift kartasi ko'rinishida (grid/list).
  if (product.category?.kind === 'telegram_gifts' && variant !== 'stars') {
    return <GiftCard product={product} />;
  }
  if (variant === 'hero') return <HeroCard product={product} />;
  if (variant === 'stars') return <StarsCard product={product} />;
  if (variant === 'grid') return <GridCard product={product} />;
  return <ListCard product={product} />;
}

/* ---------------- VIP variant — standout premium card ---------------- */

// Gold icon plate: the Galochka (Instagram verified badge) as the main icon
// with a small crown perched on top.
function VipGoldIcon({ size }: { size: number }) {
  const plate = Math.round(size * 1.45);
  return (
    <div className="relative shrink-0" style={{ width: plate, height: plate }}>
      <motion.div
        animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.85, 0.5] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(255,200,80,0.55), transparent 70%)', filter: 'blur(10px)' }}
      />
      <motion.div
        initial={{ scale: 0.7, rotate: -8 }}
        animate={{ scale: 1, rotate: 0, y: [0, -3, 0] }}
        transition={{
          scale: { type: 'spring', stiffness: 260, damping: 16 },
          y: { duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.3 },
        }}
        className="relative flex h-full w-full items-center justify-center rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, #FFE08A 0%, #E0A93B 60%, #B8862A 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 20px rgba(224,169,59,0.45)',
        }}
      >
        {/* Galochka (verified badge) as the main icon */}
        <CategoryIcon kind="instagram_verification" size={size} />
      </motion.div>
      {/* Crown perched on top */}
      <motion.span
        className="absolute left-1/2 z-10 -translate-x-1/2 leading-none"
        style={{ top: -Math.round(size * 0.28), fontSize: Math.round(size * 0.5), filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }}
        animate={{ rotate: [-6, 6, -6], y: [0, -2, 0] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
      >
        👑
      </motion.span>
    </div>
  );
}

function VipCard({
  product,
  variant = 'list',
}: {
  product: Product;
  variant?: 'list' | 'grid' | 'stars' | 'hero';
}) {
  const lang = useAuthStore((s) => s.language) as 'uz' | 'ru' | 'en';
  const name = pickLocalized(product, 'name', lang);
  // The full description is a multi-line bullet list; on the card we only want
  // the short intro (first non-empty line), not every bullet.
  const intro =
    pickLocalized(product, 'description', lang)
      ?.split('\n')
      .map((l) => l.trim())
      .find(Boolean) ?? '';
  const disc = discountPercent(product.price, product.oldPrice);
  const isUsd = product.meta?.currency === 'USD';
  const money = (v: string) =>
    isUsd ? `$${Number(v).toLocaleString('en-US')}` : formatUZS(v);
  const ctaLabel = lang === 'ru' ? 'Купить' : lang === 'en' ? 'Buy' : 'Sotib olish';
  const unlimitedLabel = lang === 'ru' ? 'Бессрочно' : lang === 'en' ? 'Unlimited' : 'Muddat cheksiz';
  const unlimitedShort = lang === 'ru' ? 'Безлимит' : lang === 'en' ? 'Unlimited' : 'Cheksiz';
  const isGrid = variant === 'grid';

  const frame = (
    <Link
      to={`/product/${product.id}`}
      onClick={() => haptic('selection')}
      className="relative block overflow-hidden rounded-2xl p-[1.5px]"
      style={{
        background: 'linear-gradient(135deg, #FFE08A 0%, #E0A93B 35%, #B8862A 60%, #FFE08A 100%)',
        boxShadow: '0 18px 44px rgba(224,169,59,0.40), 0 4px 12px rgba(0,0,0,0.45)',
      }}
    >
      <div
        className={cn('relative overflow-hidden rounded-[15px]', isGrid ? 'p-3 pt-6' : 'p-4')}
        style={{ background: 'linear-gradient(135deg, #2A2113 0%, #1A150C 55%, #120F09 100%)' }}
      >
        {/* Sheen sweep */}
        <motion.div
          className="pointer-events-none absolute inset-0"
          initial={{ x: '-120%' }}
          animate={{ x: '120%' }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.4 }}
          style={{ background: 'linear-gradient(105deg, transparent 35%, rgba(255,224,138,0.18) 50%, transparent 65%)' }}
        />
        {/* Corner glow */}
        <div
          className="pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,200,80,0.35), transparent 65%)', filter: 'blur(10px)' }}
        />

        {/* Discount ribbon */}
        {disc !== null && (
          <div
            className="absolute top-0 right-0 z-10 rounded-bl-xl px-2 py-0.5 text-[10px] font-extrabold text-[#3a2a06]"
            style={{
              background: 'linear-gradient(135deg, #FFE08A 0%, #F5C24B 60%, #E0A93B 100%)',
              boxShadow: '0 6px 14px rgba(224,169,59,0.5)',
            }}
          >
            −{disc}%
          </div>
        )}

        {isGrid ? (
          /* ===== Compact vertical layout for 2-col grid ===== */
          <div className="relative flex flex-col items-center text-center gap-2">
            <VipGoldIcon size={34} />
            <div className="flex flex-wrap items-center justify-center gap-1">
              <span
                className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[8.5px] font-extrabold uppercase tracking-wide text-[#3a2a06]"
                style={{ background: 'linear-gradient(135deg, #FFE08A, #E0A93B)' }}
              >
                ⭐ VIP
              </span>
              <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-400/10 px-1.5 py-0.5 text-[8.5px] font-bold text-amber-200 ring-1 ring-amber-400/25 whitespace-nowrap">
                ♾ {unlimitedShort}
              </span>
            </div>
            <h3
              className="font-extrabold leading-tight text-[13.5px] line-clamp-1 w-full"
              style={{
                background: 'linear-gradient(180deg, #FFF6DF 0%, #F5C24B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {name}
            </h3>
            <div className="flex items-baseline justify-center gap-2">
              <span
                className="text-[18px] font-extrabold leading-none number-tabular"
                style={{
                  background: 'linear-gradient(180deg, #FFF6DF 0%, #FFD24A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {money(product.price)}
              </span>
              {product.oldPrice && (
                <span className="text-[11px] text-amber-200/50 line-through leading-none number-tabular">
                  {money(product.oldPrice)}
                </span>
              )}
            </div>
            <span
              className="mt-0.5 inline-flex w-full items-center justify-center gap-1 rounded-full py-1.5 text-[11px] font-extrabold leading-none text-[#3a2a06]"
              style={{
                background: 'linear-gradient(135deg, #FFE08A 0%, #F5C24B 55%, #E0A93B 100%)',
                boxShadow: '0 6px 16px rgba(224,169,59,0.5), inset 0 1px 0 rgba(255,255,255,0.4)',
              }}
            >
              {ctaLabel}
              <span className="text-[13px] leading-none">›</span>
            </span>
          </div>
        ) : (
          /* ===== Horizontal layout for hero / list ===== */
          <div className="relative flex items-stretch gap-3.5">
            <div className="self-center pt-1">
              <VipGoldIcon size={40} />
            </div>
            <div className="min-w-0 flex-1 flex flex-col justify-between gap-1.5 py-0.5">
              <div>
                <div className="flex items-center gap-1.5">
                  <span
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-widest text-[#3a2a06]"
                    style={{ background: 'linear-gradient(135deg, #FFE08A, #E0A93B)' }}
                  >
                    ⭐ VIP
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-md bg-amber-400/10 px-1.5 py-0.5 text-[9.5px] font-bold text-amber-200 ring-1 ring-amber-400/25">
                    ♾ {unlimitedLabel}
                  </span>
                </div>
                <h3
                  className="mt-1.5 font-extrabold leading-snug text-[15px] line-clamp-1"
                  style={{
                    background: 'linear-gradient(180deg, #FFF6DF 0%, #F5C24B 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {name}
                </h3>
                {intro && (
                  <p className="mt-0.5 text-[11px] leading-snug text-amber-100/55 line-clamp-2">
                    {intro}
                  </p>
                )}
              </div>

              <div className="flex items-end justify-between gap-2">
                <div className="flex flex-col">
                  <span
                    className="text-[19px] font-extrabold leading-none number-tabular"
                    style={{
                      background: 'linear-gradient(180deg, #FFF6DF 0%, #FFD24A 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      filter: 'drop-shadow(0 1px 4px rgba(255,200,80,0.35))',
                    }}
                  >
                    {money(product.price)}
                  </span>
                  {product.oldPrice && (
                    <span className="mt-0.5 text-[11px] text-amber-200/50 line-through leading-none number-tabular">
                      {money(product.oldPrice)}
                    </span>
                  )}
                </div>
                <span
                  className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-extrabold leading-none text-[#3a2a06] translate-y-1.5"
                  style={{
                    background: 'linear-gradient(135deg, #FFE08A 0%, #F5C24B 55%, #E0A93B 100%)',
                    boxShadow: '0 6px 16px rgba(224,169,59,0.5), inset 0 1px 0 rgba(255,255,255,0.4)',
                  }}
                >
                  {ctaLabel}
                  <span className="text-[14px] leading-none">›</span>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Link>
  );

  return (
    <motion.div whileTap={{ scale: 0.985 }} {...ENTER}>
      {frame}
    </motion.div>
  );
}

/* ---------------- Hero variant — premium featured card ---------------- */
const HERO_THEME: Record<
  CategoryKind,
  { bg: string; halo: string; accent: string; ctaGradient: string; sparkle: string }
> = {
  telegram_nft: {
    bg: 'linear-gradient(135deg, rgba(139, 92, 246, 0.20) 0%, rgba(56, 189, 248, 0.10) 100%)',
    halo: 'rgba(139, 92, 246, 0.5)',
    accent: '#C084FC',
    ctaGradient: 'linear-gradient(135deg, #8B5CF6, #38BDF8)',
    sparkle: '#E9D5FF',
  },
  telegram_verification: {
    bg: 'linear-gradient(135deg, rgba(42, 171, 238, 0.20) 0%, rgba(90, 200, 250, 0.10) 100%)',
    halo: 'rgba(42, 171, 238, 0.5)',
    accent: '#5AC8FA',
    ctaGradient: 'linear-gradient(135deg, #2AABEE, #5AC8FA)',
    sparkle: '#BFE3FF',
  },
  instagram_verification: {
    bg: 'linear-gradient(135deg, rgba(244, 63, 94, 0.16) 0%, rgba(168, 85, 247, 0.10) 50%, rgba(56, 189, 248, 0.14) 100%)',
    halo: 'rgba(168, 85, 247, 0.45)',
    accent: '#A78BFA',
    ctaGradient: 'linear-gradient(135deg, #EC4899, #A78BFA)',
    sparkle: '#F9A8D4',
  },
  telegram_premium: {
    bg: 'linear-gradient(135deg, rgba(127, 119, 221, 0.18) 0%, rgba(56, 189, 248, 0.12) 100%)',
    halo: 'rgba(127, 119, 221, 0.5)',
    accent: '#A78BFA',
    ctaGradient: 'linear-gradient(135deg, #7F77DD, #2AABEE)',
    sparkle: '#FFD400',
  },
  telegram_stars: {
    bg: 'linear-gradient(135deg, rgba(255, 200, 80, 0.16) 0%, rgba(251, 191, 36, 0.10) 100%)',
    halo: 'rgba(251, 191, 36, 0.55)',
    accent: '#FBBF24',
    ctaGradient: 'linear-gradient(135deg, #FBBF24, #FF8800)',
    sparkle: '#FFE066',
  },
  telegram_gifts: {
    bg: 'linear-gradient(135deg, rgba(244, 63, 94, 0.16) 0%, rgba(236, 72, 153, 0.10) 100%)',
    halo: 'rgba(244, 63, 94, 0.5)',
    accent: '#F472B6',
    ctaGradient: 'linear-gradient(135deg, #EC4899, #F43F5E)',
    sparkle: '#FCA5A5',
  },
};

/* ============================================================
 *  NftCard — kolleksion NFT gift kartasi (to'liq kenglik).
 *  Aylanuvchi holografik chegara, tier chipi, brilliant gem + uchqunlar,
 *  oltin narx pili va gradient CTA. Telegram'ning NFT gift oynasi ruhida.
 * ============================================================ */
const NFT_THEME: Record<
  'standard' | 'rare' | 'legendary' | 'custom',
  { label: { uz: string; ru: string; en: string }; halo: string; chip: string; chipText: string; cta: string; conic: string }
> = {
  standard: {
    label: { uz: 'STANDART', ru: 'СТАНДАРТ', en: 'STANDARD' },
    halo: 'rgba(56,189,248,0.45)',
    chip: 'rgba(56,189,248,0.16)',
    chipText: '#7DD3FC',
    cta: 'linear-gradient(135deg, #0EA5E9, #38BDF8)',
    conic: 'conic-gradient(from 0deg, #38BDF8, #0EA5E9, #E0F2FE, #38BDF8)',
  },
  rare: {
    label: { uz: 'NOYOB ✦', ru: 'РЕДКИЙ ✦', en: 'RARE ✦' },
    halo: 'rgba(168,85,247,0.5)',
    chip: 'rgba(168,85,247,0.18)',
    chipText: '#E9D5FF',
    cta: 'linear-gradient(135deg, #7C3AED, #A855F7)',
    conic: 'conic-gradient(from 0deg, #A855F7, #FDE68A, #7C3AED, #A855F7)',
  },
  legendary: {
    label: { uz: 'LEGENDAR ✦✦✦', ru: 'ЛЕГЕНДАРНЫЙ ✦✦✦', en: 'LEGENDARY ✦✦✦' },
    halo: 'rgba(245,179,1,0.5)',
    chip: 'rgba(245,179,1,0.18)',
    chipText: '#FFE9A3',
    cta: 'linear-gradient(135deg, #B45309, #F5B301 55%, #FDE68A)',
    conic: 'conic-gradient(from 0deg, #FDE68A, #F5B301, #7C2D12, #FFF7CC, #FDE68A)',
  },
  custom: {
    label: { uz: 'ISTALGAN ✦✦', ru: 'НА ЗАКАЗ ✦✦', en: 'CUSTOM ✦✦' },
    halo: 'rgba(244,114,182,0.5)',
    chip: 'rgba(244,114,182,0.18)',
    chipText: '#FBCFE8',
    cta: 'linear-gradient(135deg, #EC4899, #8B5CF6 55%, #38BDF8)',
    conic: 'conic-gradient(from 0deg, #F472B6, #FDE68A, #38BDF8, #A855F7, #F472B6)',
  },
};

function NftCard({ product }: { product: Product }) {
  const lang = useAuthStore((s) => s.language) as 'uz' | 'ru' | 'en';
  const name = pickLocalized(product, 'name', lang);
  const intro =
    pickLocalized(product, 'description', lang)
      ?.split('\n')
      .map((l) => l.trim())
      .find(Boolean)
      ?.replace(/^[^\p{L}\p{N}]+/u, '') ?? '';
  const tier = nftTierFromSku(product.sku);
  const th = NFT_THEME[tier];
  const disc = discountPercent(product.price, product.oldPrice);
  // Haqiqiy Telegram NFT gift rasmi (Fragment bazasidan) — imageUrl bo'lmasa
  // tier bo'yicha namunaviy rasm.
  const art = product.imageUrl || `/nft/${tier}.jpg`;
  const cta = lang === 'ru' ? 'Купить' : lang === 'en' ? 'Buy' : 'Sotib olish';
  const caption = lang === 'ru' ? 'Коллекционный · Telegram NFT' : lang === 'en' ? 'Collectible · Telegram NFT' : 'Kolleksion · Telegram NFT';

  return (
    <motion.div whileTap={{ scale: 0.985 }} {...ENTER}>
      <Link
        to={`/product/${product.id}`}
        onClick={() => haptic('selection')}
        className="relative block overflow-hidden rounded-3xl p-[1.5px]"
        style={{ boxShadow: `0 22px 48px rgba(0,0,0,0.6), 0 0 36px ${th.halo}` }}
      >
        {/* Aylanuvchi holografik chegara */}
        <motion.div
          aria-hidden
          className="absolute -inset-[45%] pointer-events-none"
          style={{ background: th.conic, opacity: 0.9 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
        />
        <div
          className="relative rounded-[22px] overflow-hidden"
          style={{ background: 'linear-gradient(180deg, #17171A 0%, #0F0F12 100%)' }}
        >
          {/* ── Rasm (hero) ── */}
          <div className="relative h-[172px] overflow-hidden">
            <img
              src={art}
              alt={name}
              className="absolute inset-0 h-full w-full object-cover"
              style={{ transform: 'scale(1.02)' }}
            />
            {/* Pastga qorayish — matn o'qilsin */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(15,15,18,0.92) 100%)' }}
            />
            {/* Uchqunlar */}
            {[
              { x: '8%', y: '14%', d: 0 },
              { x: '90%', y: '18%', d: 0.9 },
              { x: '84%', y: '62%', d: 1.6 },
              { x: '14%', y: '58%', d: 2.2 },
            ].map((sp, i) => (
              <motion.span
                key={i}
                aria-hidden
                className="absolute h-1.5 w-1.5 rounded-full"
                style={{ left: sp.x, top: sp.y, background: '#FFF7CC', boxShadow: '0 0 8px #FFE066' }}
                animate={{ opacity: [0.1, 1, 0.1], scale: [0.5, 1.3, 0.5] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: sp.d }}
              />
            ))}
            {/* Tier chipi — yuqori chap */}
            <span
              className="absolute top-3 left-3 text-[9.5px] font-black tracking-[0.18em] px-2.5 py-1 rounded-full backdrop-blur"
              style={{ background: th.chip, color: th.chipText, boxShadow: `inset 0 0 0 1px ${th.chipText}66` }}
            >
              {th.label[lang]}
            </span>
            {disc !== null && (
              <span
                className="absolute top-3 right-3 text-[10px] font-black px-2 py-1 rounded-full text-white"
                style={{ background: 'linear-gradient(135deg,#FF3B5C,#C2185B)', boxShadow: '0 4px 12px rgba(233,30,99,0.5)' }}
              >
                −{disc}%
              </span>
            )}
            {/* Nom — rasm ustida, pastda */}
            <div className="absolute inset-x-4 bottom-3">
              <div className="text-[9.5px] uppercase tracking-widest text-white/50 mb-0.5">{caption}</div>
              <h3 className="font-extrabold leading-tight text-[18px] text-white line-clamp-1" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                {name}
              </h3>
            </div>
          </div>

          {/* ── Matn + narx + tugma ── */}
          <div className="px-4 pt-3 pb-4">
            <p className="text-[12px] text-white/65 leading-snug line-clamp-2 min-h-[32px]">{intro}</p>
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="min-w-0 flex flex-col">
                <span
                  className="inline-flex w-fit items-center gap-1 px-3 py-1.5 rounded-full text-[13.5px] font-extrabold number-tabular whitespace-nowrap"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,224,102,0.22), rgba(255,180,0,0.14))',
                    boxShadow: 'inset 0 0 0 1px rgba(255,214,102,0.55), 0 4px 12px rgba(255,190,0,0.25)',
                    color: '#FFE08A',
                  }}
                >
                  {formatProductPrice(product)}
                </span>
                {product.oldPrice && (
                  <span className="mt-0.5 pl-1 text-[10px] text-text-tertiary line-through number-tabular">
                    {formatProductPrice(product, 'oldPrice')}
                  </span>
                )}
              </div>
              <span
                className="shrink-0 inline-flex items-center gap-1 px-4 py-2.5 rounded-full text-[12.5px] font-bold text-white whitespace-nowrap"
                style={{ background: th.cta, boxShadow: `0 8px 20px ${th.halo}, inset 0 1px 0 rgba(255,255,255,0.25)` }}
              >
                {cta} ›
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

/* ============================================================
 *  GiftCard — Telegram'ning haqiqiy gift kartasi uslubi.
 *  Har gift o'z rangida yumshoq radial nur, markazda "suzib" turuvchi
 *  gift stikeri, uchqunlar, pastda nom + oltin narx pili + "Yuborish" CTA.
 * ============================================================ */
const GIFT_THEME: Record<
  string,
  { halo: string; ring: string; glow: string; accent: string }
> = {
  rose: {
    halo: 'rgba(255,59,122,0.42)',
    ring: 'rgba(255,120,160,0.45)',
    glow: 'rgba(255,59,122,0.55)',
    accent: '#FF6B9D',
  },
  heart: {
    halo: 'rgba(255,45,85,0.42)',
    ring: 'rgba(255,110,130,0.45)',
    glow: 'rgba(255,45,85,0.55)',
    accent: '#FF5C7A',
  },
  default: {
    halo: 'rgba(56,189,248,0.40)',
    ring: 'rgba(125,211,252,0.45)',
    glow: 'rgba(56,189,248,0.55)',
    accent: '#7DD3FC',
  },
};

function GiftCard({ product }: { product: Product }) {
  const lang = useAuthStore((s) => s.language) as 'uz' | 'ru' | 'en';
  const name = pickLocalized(product, 'name', lang);
  const disc = discountPercent(product.price, product.oldPrice);
  const giftKind = giftKindFromSku(product.sku);
  const theme = GIFT_THEME[giftKind] ?? GIFT_THEME.default;
  const cta = lang === 'ru' ? 'Отправить' : lang === 'en' ? 'Send gift' : 'Yuborish';
  const lowStock = product.stock !== null && product.stock > 0 && product.stock <= 5;

  return (
    <motion.div whileTap={{ scale: 0.97 }} {...ENTER}>
      <Link
        to={`/product/${product.id}`}
        onClick={() => haptic('selection')}
        className="relative overflow-hidden rounded-3xl flex flex-col items-center text-center px-3 pt-4 pb-3 h-[232px]"
        style={{
          background:
            `radial-gradient(120% 90% at 50% 0%, ${theme.halo} 0%, transparent 60%), ` +
            'linear-gradient(180deg, #1F1F22 0%, #151517 100%)',
          boxShadow:
            `0 16px 36px rgba(0,0,0,0.55), 0 0 0 1px ${theme.ring}, inset 0 1px 0 rgba(255,255,255,0.08)`,
        }}
      >
        {/* Orqa fon "nur halqasi" — Telegram gift sheet'dagi kabi */}
        <div
          aria-hidden
          className="absolute left-1/2 top-[64px] -translate-x-1/2 -translate-y-1/2 h-[118px] w-[118px] rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${theme.glow} 0%, transparent 70%)`,
            filter: 'blur(14px)',
          }}
        />

        {/* Uchqunlar */}
        {[
          { x: '18%', y: '18%', d: 0 },
          { x: '80%', y: '24%', d: 0.7 },
          { x: '72%', y: '58%', d: 1.3 },
          { x: '24%', y: '54%', d: 1.9 },
        ].map((sp, i) => (
          <motion.span
            key={i}
            aria-hidden
            className="absolute h-1.5 w-1.5 rounded-full pointer-events-none"
            style={{ left: sp.x, top: sp.y, background: '#FFF3B0', boxShadow: '0 0 8px #FFE066' }}
            animate={{ opacity: [0.15, 1, 0.15], scale: [0.6, 1.25, 0.6] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', delay: sp.d }}
          />
        ))}

        {/* Chegirma lentasi */}
        {disc !== null && (
          <div
            className="absolute top-0 right-0 px-2 py-0.5 rounded-bl-xl text-[10px] font-extrabold text-white z-10"
            style={{
              background: 'linear-gradient(135deg, #FF3B5C 0%, #E91E63 60%, #C2185B 100%)',
              boxShadow: '0 4px 10px rgba(233, 30, 99, 0.45)',
            }}
          >
            −{disc}%
          </div>
        )}

        {/* Gift stikeri — suzib turadi */}
        <motion.div
          className="relative z-10 h-[96px] flex items-center justify-center"
          animate={{ y: [0, -5, 0], rotate: [0, 2, 0, -2, 0] }}
          transition={{
            y: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' },
            rotate: { duration: 5.5, repeat: Infinity, ease: 'easeInOut' },
          }}
        >
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={name}
              className="h-[84px] w-[84px] object-contain"
              style={{ filter: `drop-shadow(0 10px 18px ${theme.glow})` }}
            />
          ) : (
            <GiftIcon kind={giftKind} size={84} />
          )}
        </motion.div>

        {/* Nom */}
        <h3 className="relative z-10 mt-1 font-bold leading-tight text-[13.5px] line-clamp-1 px-1 text-white">
          {name}
        </h3>

        {/* Narx pili — Telegram Stars uslubidagi oltin kapsula */}
        <div className="relative z-10 mt-1.5 flex flex-col items-center gap-0.5">
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12.5px] font-extrabold number-tabular"
            style={{
              background: 'linear-gradient(135deg, rgba(255,224,102,0.22), rgba(255,180,0,0.14))',
              boxShadow: 'inset 0 0 0 1px rgba(255,214,102,0.55), 0 4px 12px rgba(255,190,0,0.25)',
              color: '#FFE08A',
            }}
          >
            🎁 {formatProductPrice(product)}
          </span>
          {product.oldPrice && (
            <span className="text-[10px] text-text-tertiary line-through number-tabular">
              {formatProductPrice(product, 'oldPrice')}
            </span>
          )}
        </div>

        {/* CTA — Telegram'ning ko'k "Send" pili */}
        <span
          className="relative z-10 mt-auto inline-flex items-center justify-center gap-1 w-full py-1.5 rounded-full text-[11.5px] font-bold text-white"
          style={{
            background: `linear-gradient(135deg, ${theme.accent} 0%, #2AABEE 100%)`,
            boxShadow: `0 6px 16px ${theme.halo}, inset 0 1px 0 rgba(255,255,255,0.25)`,
          }}
        >
          {cta} ›
        </span>

        {lowStock && (
          <span className="absolute top-2 left-2 z-10 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/40">
            {product.stock} {lang === 'ru' ? 'шт' : lang === 'en' ? 'left' : 'ta qoldi'}
          </span>
        )}
      </Link>
    </motion.div>
  );
}

function HeroCard({ product }: { product: Product }) {
  const lang = useAuthStore((s) => s.language) as 'uz' | 'ru' | 'en';
  const name = pickLocalized(product, 'name', lang);
  const disc = discountPercent(product.price, product.oldPrice);
  const kind = product.category?.kind as CategoryKind | undefined;
  const meta = formatMeta(product, lang);
  const isAuto = kind != null && AUTO_DELIVERY.includes(kind);
  const lowStock = product.stock !== null && product.stock > 0 && product.stock <= 5;
  const theme = kind ? HERO_THEME[kind] : HERO_THEME.telegram_premium;

  const ctaLabel =
    lang === 'ru' ? 'Купить' : lang === 'en' ? 'Buy' : 'Sotib olish';

  return (
    <motion.div whileTap={{ scale: 0.985 }} {...ENTER}>
      <Link
        to={`/product/${product.id}`}
        onClick={() => haptic('selection')}
        className="relative overflow-hidden rounded-2xl flex items-stretch p-3.5 pr-3 gap-3"
        style={{
          background: `${theme.bg}, linear-gradient(180deg, #1F1F22 0%, #161618 100%)`,
          boxShadow:
            '0 14px 34px rgba(0,0,0,0.55), 0 4px 10px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.07), inset 0 -1px 0 rgba(0,0,0,0.4)',
        }}
      >
        {/* Decorative corner glow */}
        <div
          className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${theme.halo}, transparent 65%)`,
            filter: 'blur(8px)',
          }}
        />
        {/* Subtle sparkle dots */}
        <div
          className="absolute top-2 right-12 w-1 h-1 rounded-full pointer-events-none"
          style={{ background: theme.sparkle, boxShadow: `0 0 5px ${theme.sparkle}` }}
        />
        <div
          className="absolute bottom-3 right-20 w-[3px] h-[3px] rounded-full pointer-events-none"
          style={{ background: theme.sparkle, boxShadow: `0 0 6px ${theme.sparkle}`, opacity: 0.6 }}
        />

        {/* Discount ribbon — sits at the ENDING (top-right) corner so it
            doesn't visually clash with the icon on the left. Rounded bottom-
            left mirrors the corner so the ribbon reads as a folded tag. */}
        {disc !== null && (
          <div
            className="absolute top-0 right-0 px-2 py-0.5 rounded-bl-xl text-[10.5px] font-extrabold text-white z-10"
            style={{
              background: 'linear-gradient(135deg, #FF3B5C 0%, #E91E63 60%, #C2185B 100%)',
              boxShadow: '0 6px 14px rgba(233, 30, 99, 0.45)',
              textShadow: '0 1px 2px rgba(0,0,0,0.4)',
            }}
          >
            −{disc}%
          </div>
        )}

        {/* Icon with animated halo */}
        {kind && (
          <div className="relative shrink-0 self-center">
            <motion.div
              animate={{ scale: [1, 1.08, 1], opacity: [0.55, 0.85, 0.55] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                background: `radial-gradient(circle, ${theme.halo} 0%, transparent 70%)`,
                filter: 'blur(10px)',
              }}
            />
            <motion.div
              initial={{ scale: 0.7, rotate: -10 }}
              animate={{ scale: 1, rotate: 0, y: [0, -3, 0] }}
              transition={{
                scale: { type: 'spring', stiffness: 260, damping: 16 },
                rotate: { type: 'spring', stiffness: 260, damping: 16 },
                y: { duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.4 },
              }}
              className="relative"
            >
              {kind === 'telegram_gifts' ? (
                <GiftIcon kind={giftKindFromSku(product.sku)} size={56} />
              ) : (
                <CategoryIcon kind={kind} size={56} />
              )}
            </motion.div>
          </div>
        )}

        {/* Main content */}
        <div className="relative flex-1 min-w-0 flex flex-col justify-between py-0.5 gap-1.5">
          <div>
            <h3
              className="font-bold leading-snug text-[14px] line-clamp-2 pr-2"
              style={{ color: 'rgba(255,255,255,0.97)' }}
            >
              {name}
            </h3>

            {(meta || isAuto || lowStock) && (
              <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                {meta && <MetaChip compact>{meta}</MetaChip>}
                {isAuto && <AutoChip lang={lang} compact />}
                {lowStock && <StockChip stock={product.stock!} lang={lang} compact />}
              </div>
            )}
          </div>

          <div className="flex items-end justify-between gap-2">
            <div className="flex flex-col">
              <span
                className="text-[16px] font-extrabold leading-none number-tabular"
                style={{
                  background: 'linear-gradient(180deg, #FFFFFF 0%, #BFE3FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 1px 4px rgba(80,180,255,0.3))',
                }}
              >
                {formatProductPrice(product)}
              </span>
              {product.oldPrice && (
                <span className="text-[10px] text-text-tertiary line-through leading-none number-tabular mt-0.5">
                  {formatProductPrice(product, 'oldPrice')}
                </span>
              )}
            </div>

            {/* CTA pill — nudged a few px below the price baseline so it
                sits flush with the card's bottom edge instead of looking like
                it's hovering at the same line as the numbers. */}
            <span
              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-bold text-white leading-none translate-y-1.5"
              style={{
                background: theme.ctaGradient,
                boxShadow: `0 6px 16px ${theme.halo}, inset 0 1px 0 rgba(255,255,255,0.25)`,
              }}
            >
              {ctaLabel}
              <span className="text-[14px] leading-none">›</span>
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

/* ---------------- List variant ---------------- */
function ListCard({ product }: { product: Product }) {
  const lang = useAuthStore((s) => s.language) as 'uz' | 'ru' | 'en';
  const name = pickLocalized(product, 'name', lang);
  const disc = discountPercent(product.price, product.oldPrice);
  const kind = product.category?.kind as CategoryKind | undefined;
  const meta = formatMeta(product, lang);
  const lowStock = product.stock !== null && product.stock > 0 && product.stock <= 5;
  const isAuto = kind != null && AUTO_DELIVERY.includes(kind);

  return (
    <motion.div whileTap={{ scale: 0.98 }} {...ENTER}>
      <Link
        to={`/product/${product.id}`}
        onClick={() => haptic('selection')}
        className={`${CARD_SURFACE} flex items-stretch gap-3 p-3.5 pr-3`}
        style={{ boxShadow: CARD_SHADOW }}
      >
        {disc !== null && <DiscountRibbon pct={disc} />}

        {kind && (
          <div className="shrink-0 self-center">
            <CategoryIcon kind={kind} size={48} />
          </div>
        )}

        <div className="flex-1 min-w-0 flex flex-col gap-1.5 py-0.5">
          <h3 className="font-semibold leading-snug text-[14px] line-clamp-2 pr-10">
            {name}
          </h3>

          {(meta || isAuto || lowStock) && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {meta && <MetaChip>{meta}</MetaChip>}
              {isAuto && <AutoChip lang={lang} />}
              {lowStock && <StockChip stock={product.stock!} lang={lang} />}
            </div>
          )}

          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[16px] font-bold leading-none number-tabular">
              {formatProductPrice(product)}
            </span>
            {product.oldPrice && (
              <span className="text-[11px] text-text-tertiary line-through leading-none number-tabular">
                {formatProductPrice(product, 'oldPrice')}
              </span>
            )}
          </div>
        </div>

        <span className="text-text-secondary text-xl self-center shrink-0">›</span>
      </Link>
    </motion.div>
  );
}

/* ---------------- Grid variant — premium card (2-col) ----------------
 *
 * Same visual language as the HeroCard (themed gradient + glow halo +
 * animated icon + CTA pill), just packed into a 2-column layout. Keeps
 * brand identity consistent so a 1-Oylik card in grid mode reads the
 * same way as a featured card on the home page.
 */
function GridCard({ product }: { product: Product }) {
  const lang = useAuthStore((s) => s.language) as 'uz' | 'ru' | 'en';
  const name = pickLocalized(product, 'name', lang);
  const disc = discountPercent(product.price, product.oldPrice);
  const kind = product.category?.kind as CategoryKind | undefined;
  const lowStock = product.stock !== null && product.stock > 0 && product.stock <= 5;
  const isAuto = kind != null && AUTO_DELIVERY.includes(kind);
  const theme = kind ? HERO_THEME[kind] : HERO_THEME.telegram_premium;

  return (
    <motion.div whileTap={{ scale: 0.97 }} {...ENTER}>
      <Link
        to={`/product/${product.id}`}
        onClick={() => haptic('selection')}
        className="relative overflow-hidden rounded-2xl flex flex-col items-center text-center px-3 pt-3 pb-3 h-[210px]"
        style={{
          // Same themed gradient as the hero card, layered over the card base.
          background: `${theme.bg}, linear-gradient(180deg, #1F1F22 0%, #161618 100%)`,
          boxShadow:
            '0 14px 32px rgba(0,0,0,0.55), 0 4px 8px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.4)',
        }}
      >
        {/* Decorative corner glow */}
        <div
          className="absolute -top-12 -right-10 w-28 h-28 rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${theme.halo}, transparent 65%)`,
            filter: 'blur(10px)',
          }}
        />

        {/* Discount ribbon — top-right ending corner */}
        {disc !== null && (
          <div
            className="absolute top-0 right-0 px-2 py-0.5 rounded-bl-xl text-[10px] font-extrabold text-white z-10"
            style={{
              background: 'linear-gradient(135deg, #FF3B5C 0%, #E91E63 60%, #C2185B 100%)',
              boxShadow: '0 4px 10px rgba(233, 30, 99, 0.45)',
              textShadow: '0 1px 2px rgba(0,0,0,0.4)',
            }}
          >
            −{disc}%
          </div>
        )}

        {/* ─── Title ─── */}
        <h3 className="relative font-bold leading-tight text-[14px] line-clamp-1 mb-1.5 px-1 text-white">
          {name}
        </h3>

        {/* ─── Price + old price ─── */}
        <div className="relative flex flex-col items-center gap-0.5 mb-2">
          <span
            className="text-[16px] font-extrabold leading-none number-tabular text-white"
            style={{
              textShadow:
                '0 1px 2px rgba(0,0,0,0.6), 0 0 12px rgba(255,255,255,0.12)',
            }}
          >
            {formatProductPrice(product)}
          </span>
          {product.oldPrice && (
            <span className="text-[11px] text-text-tertiary line-through leading-none number-tabular">
              {formatProductPrice(product, 'oldPrice')}
            </span>
          )}
        </div>

        {/* ─── Big icon centered ─── */}
        {kind && (
          <div className="relative flex-1 flex items-center justify-center w-full py-2">
            {/* Soft glow behind icon */}
            <div
              className="absolute h-20 w-20 rounded-full pointer-events-none"
              style={{
                background: `radial-gradient(circle, ${theme.halo} 0%, transparent 70%)`,
                filter: 'blur(14px)',
              }}
            />
            <motion.div
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="relative"
            >
              {kind === 'telegram_gifts' ? (
                <GiftIcon kind={giftKindFromSku(product.sku)} size={72} />
              ) : (
                <CategoryIcon kind={kind} size={72} />
              )}
            </motion.div>
          </div>
        )}

        {/* ─── CTA pill — themed gradient, glowing edge ─── */}
        <div className="relative w-full mt-2 flex justify-center">
          <span
            className="relative inline-flex items-center justify-center gap-1 min-w-[100px] px-4 py-2 rounded-full text-[12px] font-bold leading-none text-white overflow-hidden"
            style={{
              // Same gradient + glow language as the hero card's CTA, scaled
              // for the grid card. Picks up the category's accent so each
              // tile feels visually coherent with its brand.
              background: theme.ctaGradient,
              boxShadow: `0 8px 20px ${theme.halo}, inset 0 1px 0 rgba(255,255,255,0.28), inset 0 -1px 0 rgba(0,0,0,0.18)`,
              textShadow: '0 1px 2px rgba(0,0,0,0.35)',
            }}
          >
            {/* Subtle traveling sheen so the pill feels alive */}
            <motion.span
              className="absolute inset-0 pointer-events-none"
              animate={{ x: ['-130%', '130%'] }}
              transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                background:
                  'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.22) 50%, transparent 65%)',
              }}
            />
            <span className="relative">
              {lang === 'ru' ? 'Купить' : lang === 'en' ? 'Buy' : 'Sotib olish'}
            </span>
            <span className="relative text-[13px] leading-none">›</span>
          </span>
        </div>

        {/* Optional auto/low-stock chips, kept subtle in the corner so they
            don't crowd the focal column. Only renders when at least one
            applies; positioned absolutely so it never disturbs spacing. */}
        {(isAuto || lowStock) && (
          <div className="absolute left-2 top-2 flex flex-col gap-1 z-[5]">
            {isAuto && <AutoChip lang={lang} compact />}
            {lowStock && <StockChip stock={product.stock!} lang={lang} compact />}
          </div>
        )}
      </Link>
    </motion.div>
  );
}

/* ---------------- Stars variant — golden-themed, qty-focused ---------------- */
function StarsCard({ product }: { product: Product }) {
  const qty = product.quantity ?? 0;

  return (
    <motion.div whileTap={{ scale: 0.96 }} {...ENTER}>
      <Link
        to={`/product/${product.id}`}
        onClick={() => haptic('selection')}
        className="product-card relative overflow-hidden rounded-2xl flex flex-col items-center text-center px-3 pt-3.5 pb-3 h-[195px] ring-1 ring-warning/20"
        style={{
          background:
            'linear-gradient(180deg, rgba(58,38,8,0.6) 0%, rgba(28,22,10,0.95) 60%, rgba(18,14,8,1) 100%)',
          boxShadow:
            '0 14px 32px rgba(0,0,0,0.55), 0 0 22px rgba(255,180,0,0.10), inset 0 1px 0 rgba(255,220,140,0.12), inset 0 -1px 0 rgba(0,0,0,0.45)',
        }}
      >
        {/* Golden glow halo behind star */}
        <div
          className="absolute inset-x-0 top-1 h-24 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center top, rgba(255,180,0,0.28), transparent 70%)',
            filter: 'blur(10px)',
          }}
        />

        {/* Subtle corner sparkles */}
        <div className="absolute top-2 left-2 w-1.5 h-1.5 rounded-full bg-warning/70 blur-[1px]" />
        <div className="absolute bottom-10 right-3 w-1 h-1 rounded-full bg-warning/60 blur-[0.5px]" />

        {product.isFeatured && <StarBadge />}

        <motion.div
          initial={{ scale: 0.6, rotate: -10, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1, y: [0, -3, 0] }}
          transition={{
            scale: { type: 'spring', stiffness: 260, damping: 16 },
            rotate: { type: 'spring', stiffness: 260, damping: 16 },
            opacity: { duration: 0.35 },
            y: { duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: 0.5 },
          }}
          className="relative mt-0.5 mb-0.5"
        >
          <CategoryIcon kind="telegram_stars" size={50} />
        </motion.div>

        <div
          className="relative font-extrabold text-[24px] leading-none mb-0.5 number-tabular"
          style={{
            background: 'linear-gradient(180deg, #FFE066 0%, #FFB347 60%, #FF8800 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter:
              'drop-shadow(0 1px 1px rgba(0,0,0,0.5)) drop-shadow(0 0 8px rgba(255, 180, 0, 0.55))',
          }}
        >
          {qty.toLocaleString('en-US')}
        </div>
        <div className="relative text-[9px] uppercase tracking-[0.18em] text-warning/85 font-bold mb-1.5">
          Stars
        </div>

        <PriceBlock price={product.price} oldPrice={product.oldPrice} tone="stars" />
      </Link>
    </motion.div>
  );
}

/* ---------------- Sub-components ---------------- */

/** Featured badge — glowing green checkmark in a circular pill, top-right corner. */
function StarBadge() {
  return (
    <div
      className="featured-badge absolute top-2 right-2 z-10 flex items-center justify-center h-[18px] w-[18px] rounded-full backdrop-blur-sm"
      style={{
        background:
          'radial-gradient(circle at 30% 25%, #B6FFD8 0%, #00FF8C 45%, #00C66A 100%)',
        border: '1px solid rgba(180, 255, 220, 0.9)',
      }}
      aria-label="Featured"
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden
        style={{ filter: 'drop-shadow(0 0 3px rgba(255, 255, 255, 0.95))' }}
      >
        <path
          d="M2.5 6.2 L5 8.5 L9.5 3.5"
          stroke="#fff"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

function MetaChip({ children, compact = false }: { children: ReactNode; compact?: boolean }) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-md font-semibold bg-white/[0.06] ring-1 ring-white/10 text-white/90 leading-none',
        compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]',
      ].join(' ')}
    >
      {children}
    </span>
  );
}

function AutoChip({ lang, compact = false }: { lang: 'uz' | 'ru' | 'en'; compact?: boolean }) {
  const label = lang === 'ru' ? 'Авто' : lang === 'en' ? 'Auto' : 'Auto';
  return (
    <span
      className={[
        'inline-flex items-center gap-0.5 rounded-md font-semibold bg-white/[0.06] ring-1 ring-white/10 text-white/85 leading-none',
        compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]',
      ].join(' ')}
    >
      ⚡{compact ? '' : ` ${label}`}
    </span>
  );
}

function StockChip({
  stock,
  lang,
  compact = false,
}: {
  stock: number;
  lang: 'uz' | 'ru' | 'en';
  compact?: boolean;
}) {
  const label = compact
    ? String(stock)
    : lang === 'ru'
    ? `Осталось ${stock}`
    : lang === 'en'
    ? `${stock} left`
    : `${stock} ta qoldi`;
  return (
    <span
      className={[
        'inline-flex items-center rounded-md font-bold bg-white/[0.04] ring-1 ring-white/15 text-white/90 leading-none',
        compact ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-[10px]',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

/**
 * Compact price block used in grid/stars cards.
 * Sits on a subtle divider with its own background pill so the number reads
 * clearly against the card surface. Animates in after the icon/title.
 */
function PriceBlock({
  price,
  oldPrice,
  tone,
}: {
  price: string;
  oldPrice: string | null;
  tone: 'default' | 'stars';
}) {
  const isStars = tone === 'stars';
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18, duration: 0.32, ease: [0.2, 0.7, 0.2, 1] }}
      className="relative z-10 mt-auto w-full flex flex-col items-center gap-0.5 pt-2"
    >
      {/* Divider line above price */}
      <div
        className="absolute top-0 left-3 right-3 h-px"
        style={{
          background: isStars
            ? 'linear-gradient(90deg, transparent, rgba(255,200,80,0.35), transparent)'
            : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)',
        }}
      />
      <div
        className="flex items-baseline gap-1.5 px-2.5 py-1 rounded-lg"
        style={{
          background: 'rgba(255,255,255,0.04)',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 10px rgba(0,0,0,0.35)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <span
          className="text-[14px] font-extrabold leading-none number-tabular text-white"
          style={{
            textShadow:
              '0 1px 2px rgba(0,0,0,0.55), 0 0 10px rgba(255,255,255,0.10)',
          }}
        >
          {formatUZS(price)}
        </span>
      </div>
      {oldPrice && (
        <span className="text-[10px] text-text-tertiary line-through leading-none number-tabular mt-0.5">
          {formatUZS(oldPrice)}
        </span>
      )}
    </motion.div>
  );
}

/**
 * Discount ribbon — corner badge that replaces the old green checkmark.
 * Sits in the same top-right slot, but in pink/red gradient with glow so
 * users instantly spot promo items.
 */
function DiscountRibbon({ pct }: { pct: number }) {
  return (
    <div
      className="absolute top-2 right-2 z-10 inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-extrabold text-white leading-none number-tabular"
      style={{
        background: 'linear-gradient(135deg, #FF3B5C 0%, #E91E63 60%, #C2185B 100%)',
        boxShadow:
          '0 4px 12px rgba(233, 30, 99, 0.45), inset 0 1px 0 rgba(255,255,255,0.3)',
        textShadow: '0 1px 2px rgba(0,0,0,0.35)',
      }}
      aria-label={`Discount ${pct}%`}
    >
      −{pct}%
    </div>
  );
}

